import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Drawer } from '@mui/material';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType, DISCARD_CHANGES_MESSAGE } from 'constants/confirmationDialog';

import { openConfirmationDialogAction } from 'store/global/actions';

import { contentStyles, getDrawerStyles, wrapperStyles } from './EntityFormModal.styles';
import { EntityFormModalTestIds } from './EntityFormModal.testIds';
import EntityFormModalHead from './EntityFormModalHead';
import EntityFormModalMain from './EntityFormModalMain';

const paperProps = {
  sx: wrapperStyles,
};

const slideProps = {
  timeout: { enter: 300, exit: 300 },
  appear: true,
  tabIndex: 'unset',
};

const defaultAdditionalProps = {};

const EntityFormModal = ({
  isLoading,
  isData,
  isOpen,
  onClose,
  headTabs,
  navigationTabs,
  headValue,
  customControls,
  navigationValue,
  title,
  Content,
  withOuterClickClose = false,
  customStyles,
  additionalProps = defaultAdditionalProps,
  additionalNavigationContent,
  onSave,
  isHiddenNavigationTabs = false,
  setIsNestedEntityEditActive,
  withApply = false,
  withDiscardGuard = false,
}) => {
  const dispatch = useDispatch();
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Every user-initiated close routes through here; `onClose` stays raw for the content, which
  // closes programmatically (a failed load) where a discard prompt would make no sense.
  const onRequestClose = useCallback(() => {
    if (!withDiscardGuard || !isFormChanged) {
      onClose();

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: DISCARD_CHANGES_MESSAGE,
        onAccept: onClose,
      }),
    );
  }, [dispatch, isFormChanged, onClose, withDiscardGuard]);

  // The dirty flag used to live in EntityFormModalMain, which is mounted only while open and so
  // reset itself on close. Consumers keep THIS component mounted and just toggle isOpen, so
  // without an explicit reset a dirty close would leak into the next open of the same drawer.
  useEffect(() => {
    if (isOpen) {
      return;
    }

    setIsFormChanged(false);
  }, [isOpen]);

  const handleDrawerClose = useCallback(
    (_event, reason) => {
      if (reason === 'escapeKeyDown') {
        onRequestClose();

        return;
      }

      if (reason === 'backdropClick' && withOuterClickClose) {
        onRequestClose();
      }
    },
    [onRequestClose, withOuterClickClose],
  );

  return (
    <Drawer
      PaperProps={paperProps}
      SlideProps={slideProps}
      anchor="right"
      data-testid={EntityFormModalTestIds.MODAL}
      open={isOpen}
      sx={getDrawerStyles({ customStyles, isContentLoading })}
      onClose={handleDrawerClose}
    >
      <Box className="content" data-testid={EntityFormModalTestIds.CONTENT} sx={contentStyles}>
        {isOpen && (
          <>
            {headTabs && (
              <EntityFormModalHead tabs={headTabs} value={headValue} onClose={onRequestClose} />
            )}

            <EntityFormModalMain
              Content={Content}
              additionalNavigationContent={additionalNavigationContent}
              additionalProps={additionalProps}
              customControls={customControls}
              isData={isData}
              isDataLoading={isLoading}
              isFormChanged={isFormChanged}
              isHiddenNavigationTabs={isHiddenNavigationTabs}
              setIsContentLoading={setIsContentLoading}
              setIsFormChanged={setIsFormChanged}
              setIsNestedEntityEditActive={setIsNestedEntityEditActive}
              tabs={navigationTabs}
              title={title}
              value={navigationValue}
              withApply={withApply}
              onClose={onClose}
              onRequestClose={onRequestClose}
              onSave={onSave}
            />
          </>
        )}
      </Box>
    </Drawer>
  );
};

EntityFormModal.propTypes = {
  Content: PropTypes.elementType.isRequired,
  isData: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  headTabs: PropTypes.array,
  headValue: PropTypes.string,
  navigationTabs: PropTypes.array,
  customControls: PropTypes.node,
  navigationValue: PropTypes.string,
  title: PropTypes.node,
  additionalNavigationContent: PropTypes.node,
  withOuterClickClose: PropTypes.bool,
  customStyles: PropTypes.object,
  additionalProps: PropTypes.object,
  isHiddenNavigationTabs: PropTypes.bool,
  setIsNestedEntityEditActive: PropTypes.func,
  withApply: PropTypes.bool,
  withDiscardGuard: PropTypes.bool,
  onSave: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default memo(EntityFormModal);
